
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { UserCheck, BookOpen, Users, Zap, CheckCircle } from 'lucide-react';
import { StaffManagedUser } from '@/hooks/auth/staffUserManagement';
import { useEnhancedCourses } from '@/hooks/useEnhancedCourses';

interface StaffQuickActionsProps {
  users: StaffManagedUser[];
  onBulkApprove: (userIds: string[]) => Promise<boolean>;
  onBulkAssign: (userIds: string[], courseIds: string[]) => Promise<boolean>;
}

const StaffQuickActions: React.FC<StaffQuickActionsProps> = ({
  users,
  onBulkApprove,
  onBulkAssign
}) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [bulkApprovalOpen, setBulkApprovalOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const { courses: availableCourses, loading: coursesLoading } = useEnhancedCourses();
  const pendingUsers = users.filter(user => !user.is_approved);

  const handleUserSelection = (userId: string, checked: boolean) => {
    setSelectedUsers(prev => 
      checked 
        ? [...prev, userId]
        : prev.filter(id => id !== userId)
    );
  };

  const handleCourseSelection = (courseId: string, checked: boolean) => {
    setSelectedCourses(prev =>
      checked
        ? [...prev, courseId]
        : prev.filter(id => id !== courseId)
    );
  };

  const handleBulkApproval = async () => {
    if (selectedUsers.length === 0) return;
    
    setProcessing(true);
    setProgress(0);
    
    try {
      const success = await onBulkApprove(selectedUsers);
      if (success) {
        setSelectedUsers([]);
        setBulkApprovalOpen(false);
      }
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  const handleBulkAssignment = async () => {
    if (selectedUsers.length === 0 || selectedCourses.length === 0) return;
    
    setProcessing(true);
    setProgress(0);
    
    try {
      const success = await onBulkAssign(selectedUsers, selectedCourses);
      if (success) {
        setSelectedUsers([]);
        setSelectedCourses([]);
        setBulkAssignOpen(false);
      }
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  const selectAllPendingUsers = () => {
    const pendingUserIds = pendingUsers.map(user => user.id);
    setSelectedUsers(pendingUserIds);
  };

  const selectAllUsers = () => {
    const allUserIds = users.map(user => user.id);
    setSelectedUsers(allUserIds);
  };

  return (
    <div className="space-y-6">
      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">Bulk User Approval</h3>
                <p className="text-sm text-muted-foreground">
                  Approve multiple pending users
                </p>
              </div>
              <UserCheck className="h-8 w-8 text-green-600" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Pending users:</span>
                <Badge variant="outline">{pendingUsers.length}</Badge>
              </div>
              <Button 
                onClick={() => setBulkApprovalOpen(true)}
                disabled={pendingUsers.length === 0}
                className="w-full"
              >
                Open Bulk Approval
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">Bulk Course Assignment</h3>
                <p className="text-sm text-muted-foreground">
                  Assign courses to multiple users
                </p>
              </div>
              <BookOpen className="h-8 w-8 text-blue-600" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Available courses:</span>
                <Badge variant="outline">{availableCourses.length}</Badge>
              </div>
              <Button 
                onClick={() => setBulkAssignOpen(true)}
                disabled={users.length === 0}
                className="w-full"
              >
                Open Bulk Assignment
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">Quick Stats</h3>
                <p className="text-sm text-muted-foreground">
                  Overview of user management
                </p>
              </div>
              <Zap className="h-8 w-8 text-purple-600" />
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Total users:</span>
                <span className="font-medium">{users.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Approved:</span>
                <span className="font-medium text-green-600">
                  {users.filter(u => u.is_approved).length}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Pending:</span>
                <span className="font-medium text-orange-600">
                  {pendingUsers.length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Approval Dialog */}
      <Dialog open={bulkApprovalOpen} onOpenChange={setBulkApprovalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk User Approval</DialogTitle>
            <DialogDescription>
              Select users to approve. This will grant them access to the learning platform.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllPendingUsers}>
                Select All Pending ({pendingUsers.length})
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSelectedUsers([])}>
                Clear Selection
              </Button>
            </div>

            <div className="max-h-60 overflow-y-auto border rounded-md p-4">
              {pendingUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-600" />
                  <p>No pending users to approve</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pendingUsers.map(user => (
                    <div key={user.id} className="flex items-center space-x-3 p-2 border rounded">
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={(checked) => handleUserSelection(user.id, checked as boolean)}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                      <Badge variant="outline">Pending</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {processing && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Processing approvals...</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkApprovalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkApproval}
              disabled={selectedUsers.length === 0 || processing}
            >
              {processing ? 'Approving...' : `Approve ${selectedUsers.length} Users`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assignment Dialog */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Course Assignment</DialogTitle>
            <DialogDescription>
              Select users and courses to create assignments. This will give users access to the selected courses.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* User Selection */}
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Select Users</h3>
                <div className="flex gap-2 mb-3">
                  <Button variant="outline" size="sm" onClick={selectAllUsers}>
                    Select All ({users.length})
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedUsers([])}>
                    Clear
                  </Button>
                </div>
              </div>

              <div className="max-h-48 overflow-y-auto border rounded-md p-3">
                <div className="space-y-2">
                  {users.map(user => (
                    <div key={user.id} className="flex items-center space-x-3 p-2 border rounded">
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={(checked) => handleUserSelection(user.id, checked as boolean)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{user.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <Badge variant={user.is_approved ? 'default' : 'outline'}>
                        {user.is_approved ? 'Approved' : 'Pending'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Course Selection */}
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Select Courses</h3>
                <div className="flex gap-2 mb-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setSelectedCourses(availableCourses.map(c => c.id))}
                  >
                    Select All ({availableCourses.length})
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedCourses([])}>
                    Clear
                  </Button>
                </div>
              </div>

              <div className="max-h-48 overflow-y-auto border rounded-md p-3">
                <div className="space-y-2">
                  {availableCourses.map(course => (
                    <div key={course.id} className="flex items-center space-x-3 p-2 border rounded">
                      <Checkbox
                        checked={selectedCourses.includes(course.id)}
                        onCheckedChange={(checked) => handleCourseSelection(course.id, checked as boolean)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{course.title}</p>
                        <p className="text-sm text-muted-foreground truncate">{course.description}</p>
                      </div>
                      <Badge variant="secondary">{course.level}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {processing && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Creating assignments...</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          <div className="border-t pt-4">
            <div className="flex justify-between text-sm text-muted-foreground mb-4">
              <span>Selected users: {selectedUsers.length}</span>
              <span>Selected courses: {selectedCourses.length}</span>
              <span>Total assignments: {selectedUsers.length * selectedCourses.length}</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAssignment}
              disabled={selectedUsers.length === 0 || selectedCourses.length === 0 || processing}
            >
              {processing ? 'Assigning...' : `Create ${selectedUsers.length * selectedCourses.length} Assignments`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffQuickActions;
